// Create PushNotificationContext.js
import React, { createContext, useContext, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./AuthProvider";
import OneSignal from "react-onesignal";
import { getCountPharmacy } from "../services/Handlers/PharmacyService";

const PushNotificationContext = createContext();

export const usePushNotifications = () => {
  const context = useContext(PushNotificationContext);
  if (!context) {
    throw new Error(
      "usePushNotifications must be used within PushNotificationProvider"
    );
  }
  return context;
};

export const PushNotificationProvider = ({ children }) => {
  const { userDetail, role } = useAuth();

  const [notificationCounts, setNotificationCounts] = useState({
    pharmacy: 0,
    reservation: 0,
  });
  const [subscribed, setSubscribed] = useState(false);

  const { isLoading: isPharmacyLoading, refetch: refetchPharmacyCount } =
    useQuery(
      ["PharmacyCount", userDetail?.businessId],
      () => getCountPharmacy({ businessId: userDetail?.businessId }),
      {
        enabled: !!userDetail?.businessId,
        refetchInterval: 30000,
        onSuccess: (res) => {
          if (res.status === 200) {
            updateNotificationCount("pharmacy", res.data.count || 0);
          }
        },
        onError: (error) => {
          console.error("Failed to fetch pharmacy count:", error);
          updateNotificationCount("pharmacy", 0);
        },
      }
    );

  useEffect(() => {
    OneSignal.init({
      appId: process.env.REACT_APP_ONESIGNAL_APP_ID || "",
      allowLocalhostAsSecureOrigin: true,
      notifyButton: {
        enable: true,
      },
    })
      .then(() => {
        OneSignal.Slidedown.promptPush();
        getNotificationInfo();

        OneSignal.Notifications.addEventListener(
          "foregroundWillDisplay",
          (res) => {
            if (res) {
              refetchPharmacyCount();
            }
          }
        );

        OneSignal.Debug.setLogLevel("trace");
      })
      .catch((error) => {
        console.error("DY: OneSignal initialization failed:", error);
      });

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userDetail && userDetail.id && subscribed) {
      handleLoginOnesignal({
        user_id: userDetail.id,
      });
    }

    //eslint-disable-next-line
  }, [userDetail, subscribed]);

  useEffect(() => {
    getNotificationInfo();

    //eslint-disable-next-line
  }, [userDetail]);

  const getNotificationInfo = () => {
    Promise.all([
      OneSignal.Notifications.permission,
      OneSignal.User.PushSubscription.id,
      OneSignal.User.PushSubscription.token,
      OneSignal.User.PushSubscription.optedIn,
    ])
      .then(([isSubscribed, subscriptionId, pushToken, optedIn]) => {
        if (isSubscribed) {
          setSubscribed(true);
          // Store subscription status
          localStorage.setItem("notification", "true");

          if (userDetail && userDetail.id) {
            handleLoginOnesignal({ user_id: userDetail.id });
          }
        } else {
          setSubscribed(false);
          localStorage.setItem("notification", "false");
        }
      })
      .catch((e) => {
        console.error("DY: Issue determining whether push is enabled:", e);
        setSubscribed(false);
      });
  };

  const handleLoginOnesignal = ({ user_id }) => {
    OneSignal.login(user_id.toString()).then(() => {
      console.log("DY: success login onesignal");
    });
    addUserTags();
  };

  const addUserTags = () => {
    const tags = {};

    if (role) {
      tags.role = role.toUpperCase();
    }

    if (userDetail?.businessId) {
      tags.business_id = userDetail.businessId.toString();
    }
    OneSignal.User.addTags(tags);
  };

  const handleLogoutOnesignal = () => {
    OneSignal.logout()
      .then(() => {
        console.log("DY: External user ID removed successfully");
      })
      .catch((error) => {
        console.error("DY: Error removing external user ID:", error);
      });
  };

  const updateNotificationCount = (key, count) => {
    setNotificationCounts((prev) => ({
      ...prev,
      [key]: count,
    }));
  };

  const incrementNotificationCount = (key) => {
    setNotificationCounts((prev) => ({
      ...prev,
      [key]: (prev[key] || 0) + 1,
    }));
  };

  const clearNotificationCount = (key) => {
    setNotificationCounts((prev) => ({
      ...prev,
      [key]: 0,
    }));
  };

  const refreshNotificationCounts = () => {
    refetchPharmacyCount();
  };

  return (
    <PushNotificationContext.Provider
      value={{
        notificationCounts,
        updateNotificationCount,
        incrementNotificationCount,
        clearNotificationCount,
        refreshNotificationCounts,
        isLoading: isPharmacyLoading,
        refetchPharmacyCount,
        handleLogoutOnesignal,
        subscribed,
      }}
    >
      {children}
    </PushNotificationContext.Provider>
  );
};
